// Iter86b — Admin · Logistics Approval Rules.
//
// Configures amount + complexity thresholds that gate the to_send transition.
// Pure FE form over PUT /api/logistics-requests/approval-rules/current.
import React, { useState } from "react";
import { Save, Inbox } from "lucide-react";
import { Link } from "react-router-dom";
import { useApprovalRules, usePutApprovalRules, usePendingApprovals } from "../api/logistics";

export default function AdminLogisticsApprovalRules() {
  const q = useApprovalRules();
  const put = usePutApprovalRules();
  const pending = usePendingApprovals();
  // `override` carries the user's pending edits; we render from `form` which
  // is whichever is fresher (override > server data). Avoids an effect-based
  // copy that lint flags as a forbidden state-set-in-effect.
  const [override, setOverride] = useState(null);
  const form = override ?? q.data;
  if (!form) return null;

  const setF = (path, v) => {
    setOverride((prev) => {
      const s = prev ?? q.data;
      const next = { ...s };
      if (path.includes(".")) {
        const [a, b, c] = path.split(".");
        next[a] = { ...next[a], [b]: { ...next[a][b], [c]: v } };
      } else next[path] = v;
      return next;
    });
  };
  const save = () => put.mutate(form);

  return (
    <div className="space-y-4" data-testid="admin-logistics-approval-rules">
      <header className="flex items-baseline justify-between">
        <div>
          <h1 style={{ fontFamily: "Syne, sans-serif", fontWeight: 800, fontSize: 22, color: "var(--text-primary)" }}>Approval Rules</h1>
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "var(--text-dimmer)", letterSpacing: "1.5px", textTransform: "uppercase" }}>Gate the to_send transition · amount OR complexity</div>
        </div>
        <Link to="/admin/logistics-approval-queue" className="inline-flex items-center gap-1.5 px-3 py-2 rounded" style={btnSecondary} data-testid="admin-approval-queue-link">
          <Inbox size={13} /> PENDING QUEUE ({pending.data?.count ?? 0})
        </Link>
      </header>

      <div className="rounded-md p-4" style={card}>
        <Toggle label="Amount threshold enabled" checked={!!form.amount_enabled} onChange={(v) => setF("amount_enabled", v)} testid="rules-amount-enabled" />
        <div className="grid grid-cols-[1fr_120px] gap-3 items-center mt-2">
          <label style={lbl}>Threshold (currency-agnostic — compared against raw amount)</label>
          <input type="number" min="0" value={form.amount_threshold ?? 0} onChange={(e) => setF("amount_threshold", parseFloat(e.target.value || "0"))} className="px-2 py-1.5 rounded outline-none" style={input} data-testid="rules-amount-threshold" />
        </div>
      </div>

      <div className="rounded-md p-4" style={card}>
        <Toggle label="Complexity rule enabled" checked={!!form.complexity_enabled} onChange={(v) => setF("complexity_enabled", v)} testid="rules-complexity-enabled" />
        <div className="grid grid-cols-[1fr_120px] gap-3 items-center mt-2">
          <label style={lbl}>Trigger threshold — number of factors that must fire</label>
          <input type="number" min="1" max="5" value={form.complexity_min_triggers ?? 2} onChange={(e) => setF("complexity_min_triggers", parseInt(e.target.value || "2", 10))} className="px-2 py-1.5 rounded outline-none" style={input} data-testid="rules-complexity-min" />
        </div>
        <div className="mt-3 space-y-2">
          <FactorRow label="Stops ≥ N" name="stops" form={form} setF={setF} hasMin testid="stops" />
          <FactorRow label="ADR (hazardous goods)" name="adr" form={form} setF={setF} testid="adr" />
          <FactorRow label="Temperature-controlled" name="temperature" form={form} setF={setF} testid="temperature" />
          <FactorRow label="Customs involved" name="customs" form={form} setF={setF} testid="customs" />
          <FactorRow label="Multi-country ≥ M" name="multi_country" form={form} setF={setF} hasMin testid="multi_country" />
        </div>
      </div>

      <div className="flex gap-2">
        <button onClick={save} disabled={put.isPending} className="inline-flex items-center gap-1.5 px-3 py-2 rounded" style={btnPrimary} data-testid="admin-approval-rules-save">
          <Save size={13} /> {put.isPending ? "Saving…" : "SAVE RULES"}
        </button>
        {put.isSuccess && <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#22a35a", alignSelf: "center" }}>Saved ✓</span>}
      </div>
    </div>
  );
}

const FactorRow = ({ label, name, form, setF, hasMin, testid }) => {
  const f = form.factors?.[name] || {};
  return (
    <div className="grid grid-cols-[1fr_120px_120px] gap-3 items-center" data-testid={`rules-factor-${testid}`}>
      <Toggle label={label} checked={!!f.enabled} onChange={(v) => setF(`factors.${name}.enabled`, v)} testid={`rules-factor-${testid}-enabled`} />
      {hasMin && (
        <>
          <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "var(--text-dimmer)" }}>Min count</span>
          <input type="number" min="1" value={f.min_count ?? 1} onChange={(e) => setF(`factors.${name}.min_count`, parseInt(e.target.value || "1", 10))} className="px-2 py-1.5 rounded outline-none" style={input} data-testid={`rules-factor-${testid}-min`} />
        </>
      )}
    </div>
  );
};

const Toggle = ({ label, checked, onChange, testid }) => (
  <label className="inline-flex items-center gap-2 cursor-pointer" data-testid={testid}>
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
    <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "var(--text-primary)" }}>{label}</span>
  </label>
);

const card = { background: "var(--bg-surface)", border: "1px solid var(--border-strong)" };
const lbl = { fontFamily: "'DM Mono', monospace", fontSize: 11, color: "var(--text-dim)" };
const input = { background: "var(--bg-elevated)", border: "1px solid var(--border-strong)", color: "var(--text-primary)", fontFamily: "'DM Mono', monospace", fontSize: 11 };
const btnPrimary = { background: "#185FA5", border: "1px solid #0d3f78", color: "#ffffff", fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 11, letterSpacing: "0.4px", cursor: "pointer" };
const btnSecondary = { background: "transparent", border: "1px solid #185FA5", color: "#185FA5", fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 11, letterSpacing: "0.4px", cursor: "pointer", textDecoration: "none" };
